import { useEffect, useRef } from "react";
import ConnectionData from "../../js/connection/ConnectionData";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faUsers, faDesktop } from "@fortawesome/free-solid-svg-icons";

type Props = {
    connections: ConnectionData[];
    isMobileView: boolean;
    onClose: () => void;
};

export default function ThumbnailPanel({ connections, isMobileView, onClose }: Props) {
    const thumbnailRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

    const realConnections = connections.filter(c => !c.isScreen);
    const screenConnections = connections.filter(c => c.isScreen);

    useEffect(() => {
        connections.forEach((connection) => {
            const container = thumbnailRefs.current[connection.id];
            if (!container || !connection.view) return;

            container.innerHTML = '';
            const sourceVideo = connection.view.querySelector('video');
            if (!sourceVideo) return;

            const video = document.createElement('video');
            video.srcObject = sourceVideo.srcObject;
            video.autoplay = true;
            video.muted = true;
            video.playsInline = true;
            video.style.cssText = `
                width: 100%;
                height: 100%;
                object-fit: cover;
                border-radius: 0;
                transform: ${connection.isLocal ? 'scaleX(-1)' : 'scaleX(1)'};
                display: block;
            `;
            container.appendChild(video);
        });
    }, [connections]);

    return (
        <div className="tp-root">
            <div className="tp-header">
                <div className="tp-header-left">
                    <FontAwesomeIcon icon={faUsers} className="tp-header-icon" />
                    <span className="tp-header-title">Participants</span>
                    <span className="tp-count-badge">{realConnections.length}</span>
                </div>
                <button className="tp-close-btn" onClick={onClose} title="Close">
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>

            <div className="tp-scroll">
                {realConnections.length === 0 && (
                    <div className="tp-empty">
                        <FontAwesomeIcon icon={faUsers} className="tp-empty-icon" />
                        <p>No participants yet</p>
                    </div>
                )}

                {realConnections.map((conn) => (
                    <div key={conn.id} className="tp-item">
                        <div className="tp-thumb">
                            <div
                                ref={(el) => { thumbnailRefs.current[conn.id] = el; }}
                                className="tp-video-container"
                                style={{ background: conn.color }}
                            />
                            {conn.isLocal && (
                                <span className="tp-you-badge">You</span>
                            )}
                        </div>
                        <div className="tp-info">
                            <span className="tp-name">{conn.userAlias || 'Participant'}</span>
                            <span className="tp-role">{conn.isLocal ? 'You' : 'Participant'}</span>
                        </div>
                    </div>
                ))}

                {screenConnections.length > 0 && (
                    <>
                        <div className="tp-section-label">
                            <FontAwesomeIcon icon={faDesktop} />
                            <span>Presenting</span>
                        </div>
                        {screenConnections.map((conn) => (
                            <div key={conn.id} className="tp-item">
                                <div className="tp-thumb screen">
                                    <div
                                        ref={(el) => { thumbnailRefs.current[conn.id] = el; }}
                                        className="tp-video-container"
                                        style={{ background: conn.color }}
                                    />
                                </div>
                                <div className="tp-info">
                                    <span className="tp-name">{conn.userAlias || 'Screen'}</span>
                                    <span className="tp-role screen-role">
                                        <FontAwesomeIcon icon={faDesktop} />
                                        Screen share
                                    </span>
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </div>

            <style>{`
                .tp-root {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    background: var(--hx-card);
                    color: var(--hx-ink-2);
                    font-family: inherit;
                }

                .tp-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 1rem 1.15rem;
                    border-bottom: 1px solid var(--hx-line);
                    flex-shrink: 0;
                    background: var(--hx-card);
                }

                .tp-header-left {
                    display: flex;
                    align-items: center;
                    gap: 0.6rem;
                }

                .tp-header-icon {
                    color: var(--hx-blue);
                    font-size: 0.85rem;
                }

                .tp-header-title {
                    font-size: 0.9rem;
                    font-weight: 650;
                    color: var(--hx-ink);
                    letter-spacing: -0.01em;
                }

                .tp-count-badge {
                    background: var(--hx-blue-soft);
                    color: var(--hx-blue);
                    font-size: 0.7rem;
                    font-weight: 700;
                    border-radius: 999px;
                    padding: 0.12rem 0.5rem;
                    border: 1px solid var(--hx-blue-border);
                }

                .tp-close-btn {
                    background: transparent;
                    border: none;
                    color: var(--hx-muted);
                    cursor: pointer;
                    width: 2rem;
                    height: 2rem;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.85rem;
                    transition: all 0.15s;
                }

                .tp-close-btn:hover {
                    background: var(--hx-line-2);
                    color: var(--hx-ink);
                }

                .tp-scroll {
                    flex: 1;
                    overflow-y: auto;
                    padding: 0.75rem;
                    display: flex;
                    flex-direction: column;
                    gap: 0.4rem;
                }

                .tp-scroll::-webkit-scrollbar {
                    display: block;
                    width: 4px;
                }

                .tp-scroll::-webkit-scrollbar-thumb {
                    background: var(--hx-line);
                    border-radius: 4px;
                }

                .tp-empty {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 0.75rem;
                    padding: 2rem;
                    text-align: center;
                    color: var(--hx-muted);
                }

                .tp-empty-icon {
                    font-size: 1.75rem;
                    opacity: 0.5;
                    color: var(--hx-blue);
                }

                .tp-empty p {
                    font-size: 0.85rem;
                    margin: 0;
                }

                .tp-section-label {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.7rem;
                    font-weight: 650;
                    text-transform: uppercase;
                    letter-spacing: 0.08em;
                    color: var(--hx-muted);
                    padding: 0.75rem 0.5rem 0.3rem;
                }

                .tp-item {
                    display: flex;
                    align-items: center;
                    gap: 0.85rem;
                    padding: 0.55rem;
                    border-radius: 12px;
                    transition: background 0.15s;
                    border: 1px solid transparent;
                }

                .tp-item:hover {
                    background: var(--hx-line-2);
                    border-color: var(--hx-line);
                }

                .tp-thumb {
                    width: 76px;
                    height: 54px;
                    border-radius: 10px;
                    overflow: hidden;
                    flex-shrink: 0;
                    position: relative;
                    border: 1px solid var(--hx-line);
                    background: #0b0d12;
                }

                .tp-thumb.screen {
                    border-color: var(--hx-blue-border);
                }

                .tp-video-container {
                    width: 100%;
                    height: 100%;
                    overflow: hidden;
                }

                .tp-you-badge {
                    position: absolute;
                    bottom: 4px;
                    left: 4px;
                    background: rgba(0, 0, 0, 0.65);
                    color: white;
                    font-size: 0.58rem;
                    font-weight: 650;
                    padding: 1px 5px;
                    border-radius: 4px;
                    letter-spacing: 0.03em;
                    text-transform: uppercase;
                }

                .tp-info {
                    display: flex;
                    flex-direction: column;
                    gap: 0.18rem;
                    min-width: 0;
                }

                .tp-name {
                    font-size: 0.875rem;
                    font-weight: 550;
                    color: var(--hx-ink);
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .tp-role {
                    font-size: 0.72rem;
                    color: var(--hx-muted);
                }

                .tp-role.screen-role {
                    color: var(--hx-blue);
                    display: flex;
                    align-items: center;
                    gap: 0.3rem;
                }
            `}</style>
        </div>
    );
}
